import React from "react";
import { X, AlertCircle, FileText } from "lucide-react";

interface ImportError {
  row: number;
  error: string;
  data?: any;
}

interface ImportErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  errors: ImportError[];
  totalRows: number;
}

const ImportErrorModal: React.FC<ImportErrorModalProps> = ({
  isOpen,
  onClose,
  errors,
  totalRows,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4" style={{ marginTop: 0 }}>
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <AlertCircle className="w-5 h-5 mr-2 text-red-500" />
            Import Errors Details
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="mb-4">
            <p className="text-sm text-gray-600">
              <strong>{errors.length}</strong> out of <strong>{totalRows}</strong> rows had errors
            </p>
          </div>

          <div className="space-y-4">
            {errors.map((error, idx) => (
              <div
                key={idx}
                className="border border-red-200 rounded-md p-4 bg-red-50"
              >
                <div className="flex items-start">
                  <FileText className="w-5 h-5 text-red-400 mr-3 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-red-900">
                        Row {error.row}
                      </span>
                      <span className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded">
                        {error.error}
                      </span>
                    </div>
                    
                    {error.data && (
                      <div className="mt-2 text-sm">
                        {error.data.availableColumns && error.data.availableColumns.length > 0 && (
                          <div className="mb-3">
                            <p className="text-xs font-medium text-gray-700 mb-2">
                              Available columns in this row ({error.data.availableColumns.length}):
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {error.data.availableColumns.map((col: string, colIdx: number) => (
                                <span
                                  key={colIdx}
                                  className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded font-mono"
                                >
                                  {col}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {error.data.allRowData && (
                          <div className="mt-3">
                            <p className="text-xs font-medium text-gray-700 mb-2">
                              Complete row data:
                            </p>
                            <pre className="text-xs bg-gray-100 p-3 rounded overflow-x-auto max-h-40 border border-gray-200">
                              {JSON.stringify(error.data.allRowData, null, 2)}
                            </pre>
                          </div>
                        )}
                        
                        {(error.data.name !== undefined || error.data.phone !== undefined) && (
                          <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                            <p className="font-medium text-yellow-900 mb-1">Extracted Values:</p>
                            <p><strong>Name:</strong> <span className="font-mono">{error.data.name || '(empty)'}</span></p>
                            <p><strong>Phone:</strong> <span className="font-mono">{error.data.phone || '(empty)'}</span></p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-[#264a3e] text-white rounded-md text-sm font-medium hover:bg-[#1a3329]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportErrorModal;
